import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  getCommunityLessons,
  postUseCommunityLesson,
  searchCommunityCatalog,
  type CommunityLessonSummary,
  type CommunitySearchHit,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { CommunitySearchBar } from "./CommunitySearchBar";
import { LessonPreviewTile } from "./LessonPreviewTile";
import { useUrlState } from "./useUrlState";

type CommunityLessonRow = {
  id: string;
  title: string;
  description: string | null;
  usedInCourseCount: number;
  version?: number;
  authorEmail?: string | null;
  authorName?: string | null;
};

export default function TeacherCommunityLessonsPage({ embedded = false }: { embedded?: boolean }) {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = `${location.pathname}${location.search}`;
  const [query, setQuery] = useUrlState("q", "");
  const [lessons, setLessons] = useState<CommunityLessonSummary[]>([]);
  const [searchHits, setSearchHits] = useState<CommunitySearchHit[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingId, setUsingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!bearer) return;
    setError(null);
    setLoading(true);
    try {
      if (query.trim()) {
        const { results } = await searchCommunityCatalog(bearer, { q: query.trim(), limit: 40 });
        setSearchHits(results.filter((r) => r.kind === "lesson"));
        setLessons([]);
      } else {
        const data = await getCommunityLessons(bearer, { limit: 100, offset: 0 });
        setLessons(data.lessons);
        setSearchHits(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load community lessons");
    } finally {
      setLoading(false);
    }
  }, [bearer, query]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleUse(lessonId: string) {
    if (!bearer || usingId) return;
    setUsingId(lessonId);
    setError(null);
    try {
      const { lessonId: newId } = await postUseCommunityLesson(bearer, lessonId);
      navigate(`/teacher/lessons/${newId}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not copy lesson");
    } finally {
      setUsingId(null);
    }
  }

  const rows: CommunityLessonRow[] = searchHits
    ? searchHits.map((h) => ({
        id: h.id,
        title: h.title,
        description: h.description,
        usedInCourseCount: h.usedInCourseCount ?? 0,
        authorEmail: h.authorEmail,
        authorName: h.authorName,
      }))
    : lessons;

  const body = (
    <>
      <CommunitySearchBar
        value={query}
        onChange={setQuery}
        loading={loading}
        placeholder="Search lessons. Try author:teacher@example.com"
      />

      {error ? (
        <div className="warn-banner" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="muted">No community lessons match your search yet.</p>
      ) : (
        <ul className="teacher-library-list">
          {rows.map((lesson) => (
            <li key={lesson.id} className="teacher-library-list-item">
              <div>
                <strong>{lesson.title}</strong>
                {lesson.version ? <span className="teacher-pill teacher-pill--published" style={{ marginLeft: "0.5rem" }}>v{lesson.version}</span> : null}
                {lesson.description ? <p className="muted small">{lesson.description}</p> : null}
                <p className="muted small">
                  {lesson.authorEmail ? (
                    <>
                      by{" "}
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setQuery(`author:${lesson.authorEmail}`)}
                      >
                        {lesson.authorName || lesson.authorEmail}
                      </button>
                      {" · "}
                    </>
                  ) : null}
                  Used in {lesson.usedInCourseCount} community course
                  {lesson.usedInCourseCount === 1 ? "" : "s"}
                </p>
              </div>
              <div className="teacher-library-list-actions">
                <LessonPreviewTile
                  lessonId={lesson.id}
                  lessonTitle={lesson.title}
                  returnTo={returnTo}
                  communitySource
                  size="sm"
                />
                <button
                  type="button"
                  className="btn primary tight"
                  disabled={usingId !== null}
                  onClick={() => void handleUse(lesson.id)}
                >
                  {usingId === lesson.id ? "Copying…" : "Use this lesson"}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {embedded ? null : (
        <p className="muted small" style={{ marginTop: "1rem" }}>
          Want to share your own? Publish from <Link to="/teacher/lessons">Lessons</Link>.
        </p>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <div className="teacher-page">
      <header className="teacher-page-header">
        <p className="eyebrow">Teacher · Community</p>
        <h1 className="teacher-page-title">Community lessons</h1>
        <p className="teacher-page-lede muted">
          Lessons shared by other teachers. Copy one into your library to start customizing.
        </p>
      </header>
      {body}
    </div>
  );
}
